import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ChevronDown, Calendar } from "lucide-react";
import { start } from "repl";

interface DateSelectorProps {
  onDateChange: (startDate: Date | null, endDate: Date | null) => void;
  hideToday?: boolean;
  mode?: "default" | "upcoming";
}

export const DateSelector: React.FC<DateSelectorProps> = ({
  onDateChange,
  hideToday = false,
  mode = "default",
}) => {
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const [selectedOption, setSelectedOption] = useState<string | null>(
    "Select Date",
  );

  const predefinedOptions =
    mode === "upcoming"
      ? [
          "Select Date",
          "Today",
          "Tomorrow",
          "Next 7 Days",
          "Next 30 Days",
          "This Month",
          "Next Month",
          "Custom Range",
        ]
      : [
          "Select Date",
          ...(hideToday ? [] : ["Today"]),
          "Yesterday",
          "Last 7 Days",
          "Last 30 Days",
          "This Month",
          "Last Month",
          "Custom Range",
        ];
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  const [filterStartDate, filterEndDate] = dateRange;

  const handleDateChange = useCallback(
    (start: Date | null, end: Date | null) => {
      setDateRange([start, end]);
    },
    [],
  );
  const [startDate, endDate] = dateRange;

  const datePickerRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setShowDropdown(!showDropdown);

  const handleOptionSelect = (option: string) => {
    setSelectedOption(option);
    setShowDropdown(false);

    // =========================
    // CLEAR DATE FILTER
    // =========================
    if (option === "Select Date") {
      setDateRange([null, null]);
      setShowDatePicker(false);
      onDateChange(null, null);
      return;
    }

    // =========================
    // CUSTOM RANGE
    // =========================
    if (option === "Custom Range") {
      setDateRange([null, null]);
      setShowDatePicker(true);
      return;
    }

    const now = new Date();

    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0,
      0,
    );
    const todayEnd = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999,
    );

    let filterStart: Date;
    let filterEnd: Date;

    // =========================
    // UPCOMING
    // =========================
    if (mode === "upcoming") {
      switch (option) {
        case "Today":
          filterStart = todayStart;
          filterEnd = todayEnd;
          break;

        case "Tomorrow":
          filterStart = new Date(todayStart);
          filterStart.setDate(filterStart.getDate() + 1);

          filterEnd = new Date(filterStart);
          filterEnd.setHours(23, 59, 59, 999);
          break;

        case "Next 7 Days":
          filterStart = todayStart;

          filterEnd = new Date(todayStart);
          filterEnd.setDate(filterEnd.getDate() + 6);
          filterEnd.setHours(23, 59, 59, 999);
          break;

        case "Next 30 Days":
          filterStart = todayStart;

          filterEnd = new Date(todayStart);
          filterEnd.setDate(filterEnd.getDate() + 29);
          filterEnd.setHours(23, 59, 59, 999);
          break;

        case "This Month":
          filterStart = new Date(
            now.getFullYear(),
            now.getMonth(),
            1,
            0,
            0,
            0,
            0,
          );

          filterEnd = new Date(
            now.getFullYear(),
            now.getMonth() + 1,
            0,
            23,
            59,
            59,
            999,
          );
          break;

        case "Next Month":
          filterStart = new Date(
            now.getFullYear(),
            now.getMonth() + 1,
            1,
            0,
            0,
            0,
            0,
          );

          filterEnd = new Date(
            now.getFullYear(),
            now.getMonth() + 2,
            0,
            23,
            59,
            59,
            999,
          );
          break;

        default:
          return;
      }
    } else {
      // =========================
      // DEFAULT / OTHER MODULES
      // =========================
      switch (option) {
        case "Today":
          filterStart = todayStart;
          filterEnd = todayEnd;
          break;

        case "Yesterday":
          filterStart = new Date(todayStart);
          filterStart.setDate(filterStart.getDate() - 1);

          filterEnd = new Date(todayStart);
          filterEnd.setMilliseconds(-1);
          break;

        case "Last 7 Days":
          filterStart = new Date(todayStart);
          filterStart.setDate(filterStart.getDate() - 6);

          filterEnd = todayEnd;
          break;

        case "Last 30 Days":
          filterStart = new Date(todayStart);
          filterStart.setDate(filterStart.getDate() - 29);

          filterEnd = todayEnd;
          break;

        case "This Month":
          filterStart = new Date(
            now.getFullYear(),
            now.getMonth(),
            1,
            0,
            0,
            0,
            0,
          );

          filterEnd = todayEnd;
          break;

        case "Last Month":
          filterStart = new Date(
            now.getFullYear(),
            now.getMonth() - 1,
            1,
            0,
            0,
            0,
            0,
          );

          filterEnd = new Date(
            now.getFullYear(),
            now.getMonth(),
            0,
            23,
            59,
            59,
            999,
          );
          break;

        default:
          return;
      }
    }

    // Update local state
    setDateRange([filterStart, filterEnd]);
    setShowDatePicker(false);

    // Send selected range to parent
    onDateChange(filterStart, filterEnd);
  };

  const handleCustomRangeChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setDateRange([start, end]);
    if (start && end) {
      setShowDatePicker(false);
      onDateChange(start, end);
    }
  };

  const handleReset = () => {
    setDateRange([null, null]);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setShowDatePicker(false);
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  return (
    <div className="relative w-64" ref={datePickerRef}>
      <div
        className="flex items-center gap-2 bg-white border-2 border-[#a5b7cf] rounded-lg px-3 py-2 cursor-pointer"
        onClick={toggleDropdown}
      >
        <Calendar className="w-5 h-5 text-[#61748e]" />
        <span className="flex-1 text-[#5a687c] font-medium truncate">
          {selectedOption === "Custom Range"
            ? startDate && endDate
              ? `${startDate.toLocaleDateString(
                  "en-GB",
                )} - ${endDate.toLocaleDateString("en-GB")}`
              : "Select Date Range"
            : selectedOption}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </div>

      {showDropdown && (
        <div className="absolute mt-1 w-full bg-white border rounded shadow-lg z-[9999]">
          {predefinedOptions.map((option) => (
            <div
              key={option}
              onClick={() => handleOptionSelect(option)}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
            >
              {option}
            </div>
          ))}
        </div>
      )}

      {showDatePicker && selectedOption === "Custom Range" && (
        <div className="absolute mt-2 z-[9999] bg-white p-2 border rounded shadow-lg">
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={handleCustomRangeChange}
            inline
            minDate={mode === "upcoming" ? todayStart : undefined}
            maxDate={mode === "upcoming" ? undefined : today}
          />
          {/* <div className="text-right mt-1">
            <button
              onClick={handleReset}
              className="text-blue-500 text-sm hover:underline"
              //   "mt-2 bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded"
              type="button"
            >
              Reset
            </button>
          </div> */}
        </div>
      )}
    </div>
  );
};
